import { useState, useEffect, useMemo } from 'react';

import { isOverDue } from '@app/components';
import { ICard, IList, SortOrder, SortState } from '@app/modules/task-board';

export interface FilterState {
  overdue: boolean;
  unassigned: boolean;
  noDate: boolean;
  assignedToMe: boolean;
  noLabel: boolean;
  selectedLabel: string | null;
  searchKeyword: string;
  selectedMember: string | null;
}

export interface UseMasterFilterProps {
  lists: IList[] | undefined;
  myProfileId?: number;
  boardId?: string;
}

export interface UseMasterFilterReturn {
  filters: FilterState;
  setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  sortState: SortState;
  setSortState: React.Dispatch<React.SetStateAction<SortState>>;
  filteredLists: IList[] | undefined;
  handleSortToggle: (listId: string) => void;
  resetFilters: () => void;
  updateFilter: <K extends keyof FilterState>(key: K, value: FilterState[K]) => void;
}

const initialFilterState: FilterState = {
  overdue: false,
  unassigned: false,
  noDate: false,
  assignedToMe: false,
  noLabel: false,
  selectedLabel: null,
  searchKeyword: '',
  selectedMember: null,
};

const sortCards = (cards: ICard[], sortOrder: SortOrder): ICard[] => {
  return [...cards].sort((a, b) => {
    const nameA = a.name?.toLowerCase() || '';
    const nameB = b.name?.toLowerCase() || '';
    if (sortOrder === 'asc') {
      return nameA.localeCompare(nameB);
    } else {
      return nameB.localeCompare(nameA);
    }
  });
};

const filterCard = (card: ICard, filters: FilterState, myProfileId?: number): boolean => {
  if (filters.unassigned && card.members && card.members.length > 0) {
    return false;
  }

  if (filters.overdue) {
    const endDate = card?.dueDates?.endDate?.split('T')?.[0];
    const endTime = card?.dueDates?.endTime?.split('T')?.[1];
    const isCardOverdue = endDate && endTime && isOverDue(endDate, endTime) === 'overDue';
    if (!isCardOverdue) {
      return false;
    }
  }

  if (filters.noDate && card?.dueDates?.endDate) {
    return false;
  }

  if (filters.assignedToMe && myProfileId) {
    const isAssignedToMe = card?.members?.some(
      (assignee) => String(assignee?.membersUserId) === String(myProfileId)
    );
    if (!isAssignedToMe) {
      return false;
    }
  }

  if (filters.noLabel && card?.addedLabels && card.addedLabels.length > 0) {
    return false;
  }

  if (filters.selectedMember) {
    const isMemberAssigned = card?.members?.some(
      (assignee) => String(assignee?.membersUserId) === String(filters.selectedMember)
    );
    if (!isMemberAssigned) {
      return false;
    }
  }

  if (filters.searchKeyword) {
    const matchesSearch = card?.name?.toLowerCase().includes(filters.searchKeyword.toLowerCase());
    if (!matchesSearch) {
      return false;
    }
  }

  if (filters.selectedLabel) {
    const hasSelectedLabel = card?.addedLabels?.some(
      (label) => label?.color === filters.selectedLabel
    );
    if (!hasSelectedLabel) {
      return false;
    }
  }

  return true;
};

export const useMasterFilter = ({
  lists,
  myProfileId,
  boardId,
}: UseMasterFilterProps): UseMasterFilterReturn => {
  const [filters, setFilters] = useState<FilterState>(initialFilterState);
  const [sortState, setSortState] = useState<SortState>({});

  useEffect(() => {
    if (boardId) {
      setFilters(initialFilterState);
    }
  }, [boardId]);

  const handleSortToggle = (listId: string) => {
    setSortState((prev) => {
      const currentSort = prev[listId] || 'asc';
      const nextSort: SortOrder = currentSort === 'asc' ? 'desc' : 'asc';
      return {
        ...prev,
        [listId]: nextSort,
      };
    });
  };

  const resetFilters = () => {
    setFilters(initialFilterState);
  };

  const updateFilter = <K extends keyof FilterState>(key: K, value: FilterState[K]) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const filteredLists = useMemo(() => {
    if (!lists) return undefined;

    return lists.map((list: IList) => {
      const filteredCards = list.cards.filter((card: ICard) =>
        filterCard(card, filters, myProfileId)
      );

      const sortedCards = sortCards(filteredCards, sortState[list.id] || 'asc');

      return {
        ...list,
        cards: sortedCards,
      };
    });
  }, [lists, filters, sortState, myProfileId]);

  return {
    filters,
    setFilters,
    sortState,
    setSortState,
    filteredLists,
    handleSortToggle,
    resetFilters,
    updateFilter,
  };
};
